import { getWeekStart } from '@utils/dates'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const WEEK_START_KEY = 'week-start'

export const buildSearchParam = (
  date: DateTime,
  params = new URLSearchParams(),
  weekStartsOn: 'monday' | 'sunday' = 'sunday',
) => {
  params.set(WEEK_START_KEY, getWeekStart(date, weekStartsOn).toISODate())
  return params
}

const parseDate = (
  param: string | null,
  zone?: string,
): DateTime | undefined => {
  if (param === null) return undefined

  const parsed = DateTime.fromISO(param, { zone })

  return parsed.invalidReason === null ? parsed : undefined
}

export const useWeekDatesSearchParams = ({
  zone,
  weekStartsOn,
}: {
  zone?: string
  weekStartsOn: 'sunday' | 'monday'
}) => {
  const [searchParams, setSearchParams] = useSearchParams()

  return useMemo(() => {
    const searchParam = searchParams.get(WEEK_START_KEY)
    const start = parseDate(searchParam, zone)

    const set = (start?: DateTime) => {
      if (!start) {
        setSearchParams((params) => {
          params.delete(WEEK_START_KEY)
          return params
        })
      } else {
        setSearchParams((params) =>
          buildSearchParam(start, params, weekStartsOn),
        )
      }
    }

    return {
      start,
      set,
      linkSearchParam: searchParam ? `?${WEEK_START_KEY}=${searchParam}` : '',
    }
  }, [searchParams, setSearchParams, zone, weekStartsOn])
}
